// The ¾-oblique machine body (UX §6.4): the frame is the 1×1 footprint (no upward overhang in v0.1),
// body inset 6%, top face = the upper 75% (mach.top), front strip = the bottom 25% (mach.front), outline
// 5.5% in mach.outline. Function colours go on trim, lamps and stripes only. Levels: a brass plaque with
// L lit bolts on the front face, corner rivets from L2, a function-colour trim ring from L3, gold at L4.
import type { Ctx2D } from '../ctx.ts';
import type { ArtPalette, Swatch } from '../palette.ts';
import { detailStroke, drawParts, fillP, outlineUnits, type Part, part } from '../passes.ts';
import { box, circle, ellipse, type PathFn, poly, roundPoly, rrect } from '../shapes.ts';
import type { ArtEnv } from '../types.ts';

export interface MachKit {
  c: Ctx2D;
  pal: ArtPalette;
  env: ArtEnv;
  px: number;
  /** Visible outline (units). */
  ow: number;
  level: number;
  /** Body box (units). */
  x0: number;
  x1: number;
  y0: number;
  /** Top/front split. */
  yf: number;
  y1: number;
  fn: string;
  sw(tok: string): Swatch;
  rect(
    tok: string,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number | number[],
    extra?: Partial<Part>,
  ): Part;
  ell(tok: string, cx: number, cy: number, rx: number, ry: number, extra?: Partial<Part>): Part;
  poly(
    tok: string,
    pts: readonly (readonly [number, number])[],
    r: number,
    extra?: Partial<Part>,
  ): Part;
}

export function machKit(c: Ctx2D, px: number, env: ArtEnv, level: number, fnTok: string): MachKit {
  const { pal } = env;
  const inset = pal.art.machineInset * 100;
  const x0 = inset;
  const x1 = 100 - inset;
  const y0 = inset;
  const y1 = 100 - inset;
  const yf = y0 + (y1 - y0) * env.sprites.machineTop;
  const ow = outlineUnits(pal.art.machineOutline, env.sprites.outlineMinPx, px, env.dpr);
  const bb = (pts: readonly (readonly [number, number])[]) => {
    const xs = pts.map((p) => p[0]);
    const ys = pts.map((p) => p[1]);
    return box(
      Math.min(...xs),
      Math.min(...ys),
      Math.max(...xs) - Math.min(...xs),
      Math.max(...ys) - Math.min(...ys),
    );
  };
  return {
    c,
    pal,
    env,
    px,
    ow,
    level,
    x0,
    x1,
    y0,
    yf,
    y1,
    fn: fnTok,
    sw: (t) => pal.sw(t),
    rect: (tok, x, y, w, h, r, extra = {}) =>
      part(rrect(x, y, w, h, r), pal.sw(tok), box(x, y, w, h), extra),
    ell: (tok, cx, cy, rx, ry, extra = {}) =>
      part(ellipse(cx, cy, rx, ry), pal.sw(tok), box(cx - rx, cy - ry, 2 * rx, 2 * ry), extra),
    poly: (tok, pts, r, extra = {}) =>
      part(r > 0 ? roundPoly(pts, r) : poly(pts), pal.sw(tok), bb(pts), extra),
  };
}

/** Top face + front strip; `topTok` / `frontTok` default to the steel body. */
export function bodyParts(
  m: MachKit,
  topTok = 'mach.top',
  frontTok = 'mach.front',
  outlineTok = 'mach.outline',
): Part[] {
  const r = 9;
  const outline = m.pal.hex(outlineTok);
  const w = m.x1 - m.x0;
  const top = m.rect(topTok, m.x0, m.y0, w, m.yf - m.y0 + r, [r, r, 0, 0], {
    outline,
    shade: false,
  });
  const front = m.rect(frontTok, m.x0, m.yf, w, m.y1 - m.yf, [0, 0, r, r], {
    outline,
    shade: false,
    hi: false,
    detail: (c) => {
      // function trim along the top of the front strip
      fillP(c, rrect(m.x0, m.yf, w, 3.2, 0), m.pal.hex(m.fn));
      levelTrim(m, c);
    },
  });
  return [top, front];
}

/** Level plaque (brass, L lit bolts of 4) on the front strip, right side. */
export function plaque(m: MachKit, c: Ctx2D, cx = 72): void {
  const h = (m.y1 - m.yf) * 0.62;
  const cy = m.yf + 3.2 + (m.y1 - m.yf - 3.2) / 2;
  const w = 26;
  const brass = m.sw('ui.brass');
  fillP(c, rrect(cx - w / 2, cy - h / 2, w, h, 3), brass.outline);
  fillP(c, rrect(cx - w / 2 + 1.2, cy - h / 2 + 1.2, w - 2.4, h - 2.4, 2.4), brass.base);
  for (let i = 0; i < 4; i++) {
    const bx = cx - w / 2 + 4.6 + i * ((w - 9.2) / 3);
    const lit = i < m.level;
    fillP(c, circle(bx, cy, 2.5), lit ? m.pal.hex('ui.money') : brass.shade);
    if (lit) fillP(c, circle(bx - 0.7, cy - 0.7, 0.9), m.pal.sw('ui.money').hi);
  }
}

function levelTrim(m: MachKit, c: Ctx2D): void {
  plaque(m, c);
  // front lamps in the function colour, one per side
  const cy = m.yf + 3.2 + (m.y1 - m.yf - 3.2) / 2;
  fillP(c, circle(m.x0 + 7, cy, 2.6), m.pal.sw(m.fn).base);
  fillP(c, circle(m.x0 + 6.3, cy - 0.7, 0.9), m.pal.sw(m.fn).hi);
}

/** Top-face corner rivets (L ≥ 2) and trim ring (L ≥ 3; gold at L4), drawn over the top face. */
export function topTrim(m: MachKit, c: Ctx2D): void {
  if (m.level >= 3) {
    const tok = m.level >= 4 ? 'tier.7' : m.fn;
    detailStroke(
      c,
      rrect(m.x0 + 4, m.y0 + 4, m.x1 - m.x0 - 8, m.yf - m.y0 - 6, 6),
      m.pal.hex(tok),
      m.ow * 0.55,
    );
  }
  if (m.level >= 2) {
    const s = m.sw('mach.top');
    for (const [x, y] of [
      [m.x0 + 5, m.y0 + 5],
      [m.x1 - 5, m.y0 + 5],
      [m.x0 + 5, m.yf - 4],
      [m.x1 - 5, m.yf - 4],
    ] as const) {
      fillP(c, circle(x, y, 1.9), s.shade);
      fillP(c, circle(x - 0.5, y - 0.5, 0.8), s.hi);
    }
  }
}

/** Runs the passes for machine parts (no halo; machines sit on steel plates). */
export function drawMachine(m: MachKit, parts: Part[]): void {
  drawParts(m.c, parts, {
    ow: m.ow,
    rim: false,
    halo: null,
    glow: null,
    shadeBand: m.pal.rules.shadeBand,
    hiW: 2.4,
  });
}

/** Wrap a part so `topTrim` runs right after the top face (before decorations cover it). */
export function withTopTrim(m: MachKit, parts: Part[]): Part[] {
  const [top, ...rest] = parts;
  if (!top) return parts;
  const d = top.detail;
  return [
    {
      ...top,
      detail: (c) => {
        d?.(c);
        topTrim(m, c);
      },
    },
    ...rest,
  ];
}

export function strokeP(c: Ctx2D, p: PathFn, color: string, w: number): void {
  detailStroke(c, p, color, w);
}
